import { notify } from '@yembo/yemblocks-core';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useItemImport } from '../../../_routes/Job/JobReview/ItemImport/useItemImport';
import { ItemImportContext, type PendingImport } from './context';

/**
 * Hosts the one item-import run the app can have going at a time, above the
 * job route tree so it survives navigating away from the job that
 * started it. See useItemImport for the run itself; this adds the setup-time
 * `pendingImport` (before there is anything to poll) and the minimize/restore
 * state a global progress toast needs.
 */
export const ItemImportProvider = ({ children }: { children?: ReactNode }) => {
  const [pendingImport, setPendingImport] = useState<PendingImport | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);

  const onCommitted = useCallback(() => {
    setPendingImport(null);
    setIsMinimized(false);
  }, []);

  const imp = useItemImport({ onCommitted });

  /**
   * True once a spreadsheet has reached the server: from then on, closing
   * this run loses nothing (the banner and `resume` carry it), so it never
   * needs to block another import or be deleted on close.
   */
  const isServerOwned = imp.source === 'spreadsheet' && imp.importKey !== null;

  const isBusy = pendingImport !== null || (imp.phase !== 'idle' && !isServerOwned);

  const beginPendingImport = useCallback(
    (jobKey: string, source: PendingImport['source'], files: File[]) => {
      if (isBusy) {
        notify.error('An import is already in progress. Finish reviewing it before starting another.');
        return;
      }
      // A spreadsheet being watched in the background steps aside for the new
      // one; it carries on on the server either way.
      if (isServerOwned) imp.detach();
      setIsMinimized(false);
      setPendingImport({ jobKey, source, files });
    },
    [isBusy, isServerOwned, imp]
  );

  const cancelPendingImport = useCallback(() => {
    setPendingImport(null);
  }, []);

  const cancel = useCallback(() => {
    setPendingImport(null);
    setIsMinimized(false);
    imp.cancel();
  }, [imp]);

  const close = useCallback(() => {
    setPendingImport(null);
    setIsMinimized(false);
    if (isServerOwned) imp.detach();
    else imp.cancel();
  }, [imp, isServerOwned]);

  const resume = useCallback(
    (jobKey: string, importKey: string) => {
      setIsMinimized(false);
      // Already the one being watched (e.g. its modal was minimized): just show it.
      if (imp.importKey === importKey) return;
      if (pendingImport !== null || (imp.phase !== 'idle' && !isServerOwned)) {
        notify.error('An import is already in progress. Finish reviewing it before starting another.');
        return;
      }
      imp.resume(jobKey, importKey);
    },
    [imp, isServerOwned, pendingImport]
  );

  // A minimized spreadsheet upload hands over to the banner the moment the
  // server has it, rather than leaving the toast up as a second progress UI
  // on the job's pages.
  const { detach } = imp;
  useEffect(() => {
    if (isMinimized && isServerOwned) {
      detach();
      setIsMinimized(false);
    }
  }, [isMinimized, isServerOwned, detach]);

  const minimize = useCallback(() => setIsMinimized(true), []);
  const restore = useCallback(() => setIsMinimized(false), []);

  const value = useMemo(
    () => ({
      pendingImport,
      beginPendingImport,
      cancelPendingImport,
      ...imp,
      resume,
      close,
      cancel,
      isMinimized,
      minimize,
      restore,
    }),
    [pendingImport, beginPendingImport, cancelPendingImport, imp, resume, close, cancel, isMinimized, minimize, restore]
  );

  return <ItemImportContext.Provider value={value}>{children}</ItemImportContext.Provider>;
};
